/**
 * Status vocabulary → badge tone, the eight finixt-ui draws (Feature 66, D11).
 *
 * Every enum optum-agent returns, mapped once. This replaces the eleven-entry
 * guess in the old `StatusBadge`, which silently fell through to a default for
 * anything it did not recognise.
 *
 * Grouped by enum rather than flattened into one lookup, because the same word
 * means different things in different vocabularies: a `pending` organization is
 * waiting on a human, a `pending` task is waiting on a worker, and only the
 * first is amber. `statusColor` takes the vocabulary explicitly for that reason.
 *
 * Source: the Status vocabulary artboard on the design canvas, reconciled
 * against the backend enums rather than api-client's literal unions — the two
 * have drifted in three places, each noted inline below.
 */

import type { BadgeTone } from '../ui/finixt.js';

export type StatusVocabulary =
  | 'organization'
  | 'agentLifecycle'
  | 'agentRuntime'
  | 'task'
  | 'taskSource'
  | 'capability'
  | 'wheelKind'
  | 'installJob'
  | 'federatedPartner'
  | 'document'
  | 'platformRole'
  | 'organizationRole'
  | 'flag';

const VOCABULARIES: Record<StatusVocabulary, Record<string, BadgeTone>> = {
  organization: {
    pending: 'warn',
    approved: 'info',
    rejected: 'danger',
    active: 'ok',
    inactive: 'neutral',
  },
  agentLifecycle: {
    active: 'ok',
    inactive: 'neutral',
    // `archived` is not a backend value. `AgentLifecycle` in api-client says
    // 'active' | 'archived' while core/agents/models.py says active|inactive.
    // Mapped so a stale client value still renders; the type is fixed in U14.
    archived: 'neutral',
  },
  agentRuntime: {
    running: 'ok',
    paused: 'warn',
    failed: 'danger',
    maintenance: 'violet',
    // Same drift: api-client claims 'running' | 'stopped' and AgentDetail
    // initialises to 'stopped', which the backend would reject. Fixed in U14.
    stopped: 'neutral',
  },
  task: {
    pending: 'neutral',
    running: 'info',
    // Same colour as `running` on purpose: to an operator scanning the
    // list they are one state, work that has not finished.
    streaming: 'info',
    completed: 'ok',
    failed: 'danger',
    cancelled: 'neutral',
  },
  taskSource: {
    client: 'blue',
    scheduled: 'violet',
    // `event` exists in the backend but is missing from api-client's TaskSource,
    // so the Tasks page cannot currently filter for it at all. Fixed in U13.
    event: 'info',
    operator_test: 'warn',
  },
  capability: {
    active: 'ok',
    disabled: 'neutral',
    broken: 'danger',
  },
  wheelKind: {
    capability: 'blue',
    tool: 'info',
  },
  installJob: {
    pending: 'neutral',
    running: 'info',
    installed: 'ok',
    failed: 'danger',
  },
  federatedPartner: {
    pending: 'warn',
    active: 'ok',
    rejected: 'danger',
    revoked: 'neutral',
  },
  document: {
    processing: 'info',
    ready: 'ok',
    failed: 'danger',
    needs_migration: 'warn',
  },
  platformRole: {
    'optum-platform-admin': 'blue',
    'optum-platform-viewer': 'neutral',
  },
  organizationRole: {
    'platform-client-admin': 'blue',
    'platform-client-user': 'neutral',
  },
  flag: {
    dirty: 'warn',
    dangling: 'danger',
    yanked: 'neutral',
  },
};

/**
 * Colour for one status inside one vocabulary.
 *
 * Falls back to `neutral` for an unknown value rather than throwing — a new
 * backend enum member should render plainly, not crash the page it appears on.
 */
export function statusColor(vocabulary: StatusVocabulary, status: string): BadgeTone {
  return VOCABULARIES[vocabulary][status] ?? 'neutral';
}

/** Every value a vocabulary knows — used by the tests to prove full coverage. */
export function knownStatuses(vocabulary: StatusVocabulary): string[] {
  return Object.keys(VOCABULARIES[vocabulary]);
}
